import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { EnvironmentVariables } from "@/types";
import { Plus, Trash2, AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface EditEnvVarsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialEnvVars: EnvironmentVariables[];
  onSave: (envVars: EnvironmentVariables[]) => void;
  isSaving?: boolean;
}

type EditableEnvVar = EnvironmentVariables & {
  initialKey: string;
  storedValue: string;
  isExisting: boolean;
  isDirty: boolean;
};

export function EditEnvVarsDialog({
  open,
  onOpenChange,
  initialEnvVars,
  onSave,
  isSaving = false,
}: EditEnvVarsDialogProps) {
  const [envVars, setEnvVars] = useState<EditableEnvVar[]>([]);
  const [errors, setErrors] = useState<Record<number, string>>({});

  useEffect(() => {
    if (open) {
      setEnvVars(
        initialEnvVars.map((v) => ({
          key: v.key,
          value: "",
          initialKey: v.key,
          storedValue: v.value || "",
          isExisting: true,
          isDirty: false,
        })),
      );
      setErrors({});
    }
  }, [open, initialEnvVars]);

  const addEnvVar = () => {
    setEnvVars([
      ...envVars,
      {
        key: "",
        value: "",
        initialKey: "",
        storedValue: "",
        isExisting: false,
        isDirty: false,
      },
    ]);
  };

  const removeEnvVar = (index: number) => {
    setEnvVars(envVars.filter((_, i) => i !== index));
    const newErrors = { ...errors };
    delete newErrors[index];
    setErrors(newErrors);
  };

  const updateEnvVar = (
    index: number,
    field: "key" | "value",
    value: string,
  ) => {
    const updated = [...envVars];
    const current = updated[index];
    const isExisting = current.isExisting;
    const keyChanged =
      field === "key" && value.trim() !== current.initialKey.trim();
    const valueChanged = field === "value" && value.trim() !== "";
    const isDirty = isExisting
      ? field === "value"
        ? valueChanged
        : current.isDirty || keyChanged
      : current.isDirty || keyChanged || valueChanged;
    updated[index] = {
      ...current,
      [field]: value,
      isDirty,
    };
    setEnvVars(updated);

    // Clear error for this field
    if (errors[index]) {
      const newErrors = { ...errors };
      delete newErrors[index];
      setErrors(newErrors);
    }
  };

  const validateEnvVars = () => {
    const newErrors: Record<number, string> = {};
    const keys = new Set<string>();

    envVars.forEach((envVar, index) => {
      // Validate key
      if (!envVar.key.trim()) {
        newErrors[index] = "Key is required";
      } else if (!/^[A-Z_][A-Z0-9_]*$/.test(envVar.key)) {
        newErrors[index] =
          "Key must start with a letter or underscore and contain only uppercase letters, numbers, and underscores";
      } else if (keys.has(envVar.key)) {
        newErrors[index] = "Duplicate key";
      } else {
        keys.add(envVar.key);
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateEnvVars()) {
      const validEnvVars = envVars
        .filter((v) => v.key.trim())
        .map((v) => ({
          key: v.key,
          value: v.isExisting && !v.isDirty ? v.storedValue : v.value,
        }));
      onSave(validEnvVars);
    }
  };

  const hasChanges = () => {
    if (envVars.length !== initialEnvVars.length) return true;

    return envVars.some((envVar, index) => {
      const initial = initialEnvVars[index];
      return (
        !initial ||
        envVar.key !== initial.key ||
        envVar.isDirty ||
        !envVar.isExisting
      );
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col bg-slate-900 border-cyan-500/30">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-cyan-400">
            Edit Environment Variables
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Add, edit, or remove environment variables. Changes will trigger a
            new deployment.
          </DialogDescription>
        </DialogHeader>

        {hasChanges() && (
          <Alert className="border-yellow-500/40 bg-yellow-500/10">
            <AlertTriangle className="h-4 w-4 text-yellow-400" />
            <AlertDescription className="text-yellow-200 text-sm">
              Saving changes will trigger a new deployment with updated
              environment variables.
            </AlertDescription>
          </Alert>
        )}

        <div className="flex-1 overflow-y-auto pr-2">
          <div className="space-y-4">
            {envVars.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <p className="mb-4">No environment variables configured.</p>
                <Button
                  type="button"
                  onClick={addEnvVar}
                  className="cyber-button"
                  disabled={isSaving}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Variable
                </Button>
              </div>
            ) : (
              <>
                {envVars.map((envVar, index) => (
                  <div key={index}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-slate-300">
                          KEY{" "}
                          {index === 0 && (
                            <span className="text-red-400">*</span>
                          )}
                        </Label>
                        <Input
                          value={envVar.key}
                          onChange={(e) =>
                            updateEnvVar(index, "key", e.target.value)
                          }
                          placeholder="DATABASE_URL"
                          className={`bg-slate-800/50 border-slate-700 text-slate-100 font-mono ${
                            errors[index] ? "border-red-400" : ""
                          }`}
                          disabled={isSaving}
                        />
                        {errors[index] && (
                          <p className="text-red-400 text-xs">
                            {errors[index]}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-slate-300">
                            VALUE
                          </Label>
                          <Button
                            type="button"
                            onClick={() => removeEnvVar(index)}
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-6 px-2"
                            disabled={isSaving}
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Remove
                          </Button>
                        </div>
                        <Input
                          value={
                            envVar.isExisting && !envVar.isDirty
                              ? ""
                              : envVar.value
                          }
                          onChange={(e) =>
                            updateEnvVar(index, "value", e.target.value)
                          }
                          placeholder={
                            envVar.isExisting && !envVar.isDirty
                              ? "***"
                              : "Enter value"
                          }
                          type="password"
                          className="bg-slate-800/50 border-slate-700 text-slate-100 font-mono"
                          disabled={isSaving}
                        />
                      </div>
                    </div>
                    {index < envVars.length - 1 && (
                      <Separator className="mt-4 bg-slate-700/50" />
                    )}
                  </div>
                ))}

                <div className="pt-2">
                  <Button
                    type="button"
                    onClick={addEnvVar}
                    variant="outline"
                    size="sm"
                    className="cyber-button w-full md:w-auto"
                    disabled={isSaving}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Variable
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={isSaving || !hasChanges()}
            className="cyber-button"
          >
            {isSaving ? "Saving..." : "Save & Redeploy"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
